/** @typedef {import("../../../api/types").ServiceItem} ServiceItem */
import React from "react";
import {Button, FormGroup, Input, Label, ListGroup, ListGroupItem} from "reactstrap";
import SearchSelect from "../../../shared/components/SearchSelect";
import {formatMoney} from "../../../shared/utils/format";
import FormModal from "../../../shared/components/FormModal";
import {ItemSummary} from "./OrderSummaries";
import RowActions from "../../../shared/components/RowActions";
import {useRowEditor} from "../../../hooks/useRowEditor";
import IconLabel from "../../../shared/components/IconLabel";
import {PlusIcon} from "../../../shared/components/icons";

/**
 * @typedef {{key: number, serviceItemId: string, quantity: string}} ItemRow
 * @param {{
 *   rows: ItemRow[],
 *   serviceItems: ServiceItem[],
 *   currency?: string,
 *   errors: Record<number, Record<string, string>>,
 *   onChange: (rows: ItemRow[]) => void,
 * }} props
 */
const OrderItemsEditor = ({rows, serviceItems, currency, errors, onChange}) => {
    const editor = useRowEditor(rows, onChange);
    const byId = new Map(serviceItems.map((item) => [String(item.id), item]));

    return (
        <>
            {rows.length === 0 && <p className="text-muted">Nenhum serviço adicionado.</p>}
            {rows.length > 0 && (
                <ListGroup className="mb-3">
                    {rows.map((row, index) => {
                        const item = byId.get(row.serviceItemId);
                        const quantity = Number(row.quantity);
                        const unit = Number(item?.base_value);
                        return (
                            <ListGroupItem className="d-flex align-items-start gap-2" key={row.key}>
                                <ItemSummary
                                    name={item?.name}
                                    quantity={quantity}
                                    unit={unit}
                                    total={unit * quantity}
                                    currency={currency}
                                    errors={Object.values(errors[index] ?? {})}
                                />
                                <RowActions
                                    noun="serviço"
                                    onEdit={() => editor.edit(row.key)}
                                    onRemove={() => editor.remove(row.key)}
                                />
                            </ListGroupItem>
                        );
                    })}
                </ListGroup>
            )}
            <Button color="primary" outline size="sm" onClick={editor.add}>
                <IconLabel icon={<PlusIcon />}>Adicionar serviço</IconLabel>
            </Button>

            <FormModal
                isOpen={editor.isOpen}
                toggle={editor.close}
                title={editor.current ? "Editar serviço" : "Adicionar serviço"}
                initial={{
                    serviceItemId: editor.current?.serviceItemId ?? "",
                    quantity: editor.current?.quantity ?? "1",
                }}
                canSave={(v) => Boolean(byId.has(v.serviceItemId) && Number(v.quantity) >= 1)}
                onSave={editor.save}
            >
                {(values, set) => (
                    <>
                        <FormGroup>
                            <Label for="item-service">Serviço</Label>
                            <SearchSelect
                                id="item-service"
                                autoFocus
                                ariaLabel="Serviço"
                                placeholder="Buscar serviço…"
                                emptyMessage="Nenhum serviço encontrado."
                                value={values.serviceItemId}
                                onChange={(serviceItemId) => set({serviceItemId})}
                                options={serviceItems.map((si) => ({
                                    value: String(si.id),
                                    label: `${si.name} — ${formatMoney(si.base_value, si.currency)}`,
                                    search: si.description ?? "",
                                }))}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="item-quantity">Quantidade</Label>
                            <Input
                                id="item-quantity"
                                type="number"
                                min="1"
                                step="1"
                                value={values.quantity}
                                onChange={(e) => set({quantity: e.target.value})}
                            />
                        </FormGroup>
                    </>
                )}
            </FormModal>
        </>
    );
};

export default OrderItemsEditor;
